import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Shield, Search, Plus, CheckCircle2,
  ChevronRight, FileText, Target, X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { useApp } from '../context/AppContext';
import { useTrace } from '../context/TraceContext';
import { useQualityEvents } from '../context/QualityEventsContext';
import TraceLabel from '../components/trace/TraceLabel';
import { formatDate } from '../data/mockData';
import CAPACreateDialog from '../components/quality-events/CAPACreateDialog';
import ReportExportButton from '../components/export/ReportExportButton';
import ReportSection from '../components/export/ReportSection';
import { toast } from 'sonner';

const CAPAPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { pidTraceMode } = useTrace();
  const { isReadOnly, currentRole } = useApp();
  const {
    capas,
    capaCounts,
    filterCAPAs,
    getCAPAById,
    updateCAPA,
    addCAPATask,
    completeCAPATask,
    submitCAPAForEffectiveness,
    verifyEffectiveness,
    closeCAPA,
    permissions,
    can
  } = useQualityEvents();

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedCAPA, setSelectedCAPA] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [newTaskText, setNewTaskText] = useState('');

  useEffect(() => {
    const id = searchParams.get('id');
    if (id) {
      const capa = getCAPAById(id);
      if (capa) {
        setSelectedCAPA(capa);
      }
    }
  }, [searchParams, getCAPAById]);

  useEffect(() => {
    if (selectedCAPA) {
      const updated = getCAPAById(selectedCAPA.id);
      if (updated) {
        setSelectedCAPA(updated);
      }
    }
  }, [capas, selectedCAPA, getCAPAById]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Draft': return 'bg-slate-100 text-slate-600 border-slate-200';
      case 'Open': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'In Progress': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'Implemented': return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'Effectiveness Check': return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'Closed': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical': return 'bg-red-50 text-red-700 border-red-200';
      case 'High': return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'Medium': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'Low': return 'bg-slate-100 text-slate-600 border-slate-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  const filteredCAPAs = useMemo(() => {
    return filterCAPAs({
      status: statusFilter,
      search: searchQuery
    });
  }, [filterCAPAs, statusFilter, searchQuery]);

  const handleAddTask = () => {
    if (!selectedCAPA || !newTaskText.trim()) return;
    addCAPATask(selectedCAPA.id, {
      title: newTaskText,
      description: ''
    });
    setNewTaskText('');
    toast.success('Task added');
  };

  const handleCompleteTask = (taskId) => {
    if (!selectedCAPA) return;
    completeCAPATask(selectedCAPA.id, taskId);
    toast.success('Task completed');
  };

  const handleSubmitForEffectiveness = () => {
    if (!selectedCAPA) return;
    submitCAPAForEffectiveness(selectedCAPA.id);
    toast.success('CAPA submitted for effectiveness check');
  };

  const handleVerifyEffectiveness = (result) => {
    if (!selectedCAPA) return;
    verifyEffectiveness(selectedCAPA.id, result);
    toast.success(`Effectiveness check: ${result}`);
  };

  const handleCloseCAPA = () => {
    if (!selectedCAPA) return;
    try {
      closeCAPA(selectedCAPA.id);
      toast.success('CAPA closed');
    } catch (error) {
      toast.error('Cannot close CAPA', {
        description: error.message
      });
    }
  };

  const handleCAPACreated = (newCAPA) => {
    setSelectedCAPA(newCAPA);
    navigate(`/capa?id=${newCAPA.id}`);
  };

  const CAPARow = ({ capa }) => (
    <TableRow 
      className={`cursor-pointer hover:bg-slate-50 ${selectedCAPA?.id === capa.id ? 'bg-slate-100' : ''}`}
      onClick={() => {
        setSelectedCAPA(capa);
        navigate(`/capa?id=${capa.id}`);
      }}
      data-testid={`capa-row-${capa.id}`}
    >
      <TableCell className="font-mono text-xs text-slate-500">{capa.id}</TableCell>
      <TableCell>
        <div className="max-w-xs">
          <p className="text-sm text-slate-800 truncate font-medium">{capa.title}</p>
          {capa.deviationId && (
            <Link 
              to={`/deviations?id=${capa.deviationId}`}
              className="text-xs text-[#fd4816] hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {capa.deviationId}
            </Link>
          )}
        </div>
      </TableCell>
      <TableCell>
        <Badge className={`${getStatusColor(capa.status)} text-xs`}>{capa.status}</Badge>
      </TableCell>
      <TableCell>
        <Badge className={`${getPriorityColor(capa.priority)} text-xs`}>{capa.priority}</Badge>
      </TableCell>
      <TableCell>
        <Progress value={capa.progress || 0} className="h-2 w-16 bg-slate-200" />
      </TableCell>
      <TableCell className="text-sm text-slate-600">{formatDate(capa.dueDate)}</TableCell>
      <TableCell className="text-sm text-slate-600">{capa.owner}</TableCell>
      <TableCell>
        <ChevronRight className="h-4 w-4 text-slate-400" />
      </TableCell>
    </TableRow>
  );

  const CAPADetail = ({ capa }) => {
    const { getDeviationById } = useQualityEvents();
    const linkedDeviation = capa.deviationId ? getDeviationById(capa.deviationId) : null;

    return (
      <div className="space-y-4">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="tasks">Tasks</TabsTrigger>
            <TabsTrigger value="effectiveness">Effectiveness</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4 mt-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-slate-500 font-mono mb-1">{capa.id}</p>
                <h3 className="text-lg font-medium text-slate-900">{capa.title}</h3>
              </div>
              <div className="flex gap-2">
                <Badge className={`${getPriorityColor(capa.priority)}`}>{capa.priority}</Badge>
                <Badge className={`${getStatusColor(capa.status)}`}>{capa.status}</Badge>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-slate-500">Progress</p>
                <span className="text-sm text-slate-800 font-medium">{capa.progress || 0}%</span>
              </div>
              <Progress value={capa.progress || 0} className="h-2 bg-slate-200" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-md p-3 border border-slate-200">
                <p className="text-xs text-slate-500 mb-1">Owner</p>
                <p className="text-sm text-slate-800 font-medium">{capa.owner}</p>
              </div>
              <div className="bg-white rounded-md p-3 border border-slate-200">
                <p className="text-xs text-slate-500 mb-1">Due Date</p>
                <p className="text-sm text-slate-800">{formatDate(capa.dueDate)}</p>
              </div>
              {linkedDeviation && (
                <div className="bg-white rounded-md p-3 border border-slate-200">
                  <p className="text-xs text-slate-500 mb-1">Source Deviation</p>
                  <Link 
                    to={`/deviations?id=${capa.deviationId}`}
                    className="text-sm text-[#fd4816] hover:underline font-mono"
                  >
                    {capa.deviationId}
                  </Link>
                </div>
              )}
              <div className="bg-white rounded-md p-3 border border-slate-200">
                <p className="text-xs text-slate-500 mb-1">Effectiveness Check</p>
                <p className="text-sm text-slate-800">{capa.effectivenessCheck || '30 days'}</p>
              </div>
            </div>

            {capa.correctiveActions && capa.correctiveActions.length > 0 && (
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2 font-medium">Corrective Actions</p>
                <div className="space-y-2">
                  {capa.correctiveActions.map((action, idx) => (
                    <div key={idx} className="bg-slate-50 rounded-md p-3 border border-slate-200">
                      <p className="text-sm text-slate-700">{action}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {capa.preventiveActions && capa.preventiveActions.length > 0 && (
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2 font-medium">Preventive Actions</p>
                <div className="space-y-2">
                  {capa.preventiveActions.map((action, idx) => (
                    <div key={idx} className="bg-slate-50 rounded-md p-3 border border-slate-200">
                      <p className="text-sm text-slate-700">{action}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!isReadOnly && capa.status !== 'Closed' && (
              <div className="flex gap-2 pt-2">
                {capa.status === 'In Progress' && capa.progress === 100 && (
                  <Button 
                    className="bg-[#fd4816] hover:bg-[#ea380c] text-white flex-1"
                    onClick={() => updateCAPA(capa.id, { status: 'Implemented' })}
                  >
                    Mark Implemented
                  </Button>
                )}
                {capa.status === 'Implemented' && (
                  <Button 
                    className="bg-[#fd4816] hover:bg-[#ea380c] text-white flex-1"
                    onClick={handleSubmitForEffectiveness}
                    data-testid="start-effectiveness"
                  >
                    <Target className="h-4 w-4 mr-2" />
                    Start Effectiveness Check
                  </Button>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="tasks" className="space-y-4 mt-4">
            <div className="space-y-2">
              <p className="text-xs text-slate-500 uppercase tracking-wider font-medium">Tasks</p>
              {capa.tasks && capa.tasks.length > 0 ? (
                <div className="space-y-2">
                  {capa.tasks.map((task) => (
                    <div key={task.id} className="flex items-start gap-2 bg-slate-50 rounded-md p-3 border border-slate-200">
                      <Checkbox
                        checked={task.status === 'Completed'}
                        onCheckedChange={() => handleCompleteTask(task.id)}
                        disabled={isReadOnly || task.status === 'Completed'}
                      />
                      <div className="flex-1">
                        <p className={`text-sm ${task.status === 'Completed' ? 'line-through text-slate-500' : 'text-slate-700'}`}>
                          {task.title}
                        </p>
                        {task.description && (
                          <p className="text-xs text-slate-500 mt-1">{task.description}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">No tasks yet</p>
              )}
            </div>

            {!isReadOnly && capa.status !== 'Closed' && (
              <div className="flex gap-2">
                <Input
                  value={newTaskText}
                  onChange={(e) => setNewTaskText(e.target.value)}
                  placeholder="Add new task..."
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddTask();
                    }
                  }}
                />
                <Button onClick={handleAddTask} disabled={!newTaskText.trim()}>
                  Add Task
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="effectiveness" className="space-y-4 mt-4">
            {capa.status === 'Effectiveness Check' ? (
              <div className="space-y-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-2 font-medium">Effectiveness Verification</p>
                  {can('capa:verifyEffectiveness') ? (
                    <div className="flex gap-2">
                      <Button
                        className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1"
                        onClick={() => handleVerifyEffectiveness('Pass')}
                      >
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Pass
                      </Button>
                      <Button
                        variant="outline"
                        className="border-red-200 text-red-600 hover:bg-red-50 flex-1"
                        onClick={() => handleVerifyEffectiveness('Fail')}
                      >
                        Fail
                      </Button>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500">Waiting for QA Head verification</p>
                  )}
                </div>
                {capa.effectivenessResult && (
                  <div className="bg-slate-50 rounded-md p-3 border border-slate-200">
                    <p className="text-xs text-slate-500 mb-1">Result</p>
                    <p className="text-sm text-slate-700 font-medium">{capa.effectivenessResult}</p>
                    {capa.effectivenessVerifiedBy && (
                      <p className="text-xs text-slate-500 mt-1">
                        Verified by {capa.effectivenessVerifiedBy} on {formatDate(capa.effectivenessVerifiedAt)}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ) : capa.status === 'Closed' ? (
              <div className="bg-emerald-50 rounded-md p-4 border border-emerald-200">
                <p className="text-sm font-medium text-emerald-700">CAPA Closed</p>
                {capa.effectivenessResult && (
                  <p className="text-xs text-emerald-600 mt-1">
                    Effectiveness: {capa.effectivenessResult}
                  </p>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <p>Effectiveness check not started yet.</p>
                {capa.status === 'Implemented' && (
                  <Button 
                    className="mt-4 bg-[#fd4816] hover:bg-[#ea380c] text-white"
                    onClick={handleSubmitForEffectiveness}
                  >
                    Start Effectiveness Check
                  </Button>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="capa-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            CAPA Management
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Corrective and Preventive Actions tracking with effectiveness verification
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-blue-50 text-blue-700 border border-blue-200">
            {capaCounts.open} Open
          </Badge>
          <Badge className="bg-amber-50 text-amber-700 border border-amber-200">
            {capaCounts.effectiveness} Effectiveness Check
          </Badge>
          {/* <ReportExportButton 
            title="CAPA Management Report"
            subtitle="Corrective and Preventive Actions tracking with effectiveness verification"
          /> */}
          {can('capa:create') && (
            <Button 
              className="bg-[#fd4816] hover:bg-[#ea380c] text-white" 
              onClick={() => setCreateDialogOpen(true)}
              data-testid="new-capa"
            >
              <Plus className="h-4 w-4 mr-2" />
              New CAPA
            </Button>
          )}
        </div>
      </div>

      <ReportSection id="capa-metrics" title="CAPA Metrics" description="Key performance indicators">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className={`bg-white border border-slate-200 shadow-sm relative ${pidTraceMode ? 'border-cyan-300' : ''}`}>
            <TraceLabel prd="PRD §6.4" req="SM-CAPA-001" />
            <CardContent className="p-4">
              <p className="text-xs text-slate-600 uppercase tracking-wider mb-1 font-medium">Total CAPAs</p>
              <p className="text-2xl font-bold text-slate-900">{capaCounts.total}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <p className="text-xs text-slate-600 uppercase tracking-wider mb-1 font-medium">Open CAPAs</p>
              <p className="text-2xl font-bold text-blue-600">{capaCounts.open}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <p className="text-xs text-slate-600 uppercase tracking-wider mb-1 font-medium">Aging (30+ days)</p>
              <p className="text-2xl font-bold text-red-600">{capaCounts.aging30}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <p className="text-xs text-slate-600 uppercase tracking-wider mb-1 font-medium">Pending Effectiveness</p>
              <p className="text-2xl font-bold text-amber-600">{capaCounts.effectiveness}</p>
            </CardContent>
          </Card>
        </div>
      </ReportSection>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <ReportSection id="capa-filters" title="Filters" description="Search and filter CAPAs">
            <Card className={`bg-white border border-slate-200 shadow-sm relative ${pidTraceMode ? 'border-cyan-300' : ''}`}>
              <TraceLabel prd="PRD §6.4" req="SM-CAPA-001" />
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder="Search CAPAs..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 bg-white border-slate-200 text-slate-700"
                      data-testid="capa-search"
                    />
                  </div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[200px] bg-white border-slate-200 text-slate-700">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      <SelectItem value="all" className="text-slate-700">All Status</SelectItem>
                      <SelectItem value="Draft" className="text-slate-700">Draft</SelectItem>
                      <SelectItem value="Open" className="text-slate-700">Open</SelectItem>
                      <SelectItem value="In Progress" className="text-slate-700">In Progress</SelectItem>
                      <SelectItem value="Implemented" className="text-slate-700">Implemented</SelectItem>
                      <SelectItem value="Effectiveness Check" className="text-slate-700">Effectiveness Check</SelectItem>
                      <SelectItem value="Closed" className="text-slate-700">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </ReportSection>

          <ReportSection id="capa-list" title="CAPA List" description="All CAPAs">
            <Card className={`bg-white border border-slate-200 shadow-sm relative ${pidTraceMode ? 'border-cyan-300' : ''}`}>
              <Table>
              <TableHeader>
                <TableRow className="border-slate-200 hover:bg-transparent">
                  <TableHead className="text-slate-600">ID</TableHead>
                  <TableHead className="text-slate-600">Title</TableHead>
                  <TableHead className="text-slate-600">Status</TableHead>
                  <TableHead className="text-slate-600">Priority</TableHead>
                  <TableHead className="text-slate-600">Progress</TableHead>
                  <TableHead className="text-slate-600">Due Date</TableHead>
                  <TableHead className="text-slate-600">Owner</TableHead>
                  <TableHead className="text-slate-600 w-8"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCAPAs.map(capa => (
                  <CAPARow key={capa.id} capa={capa} />
                ))}
                {filteredCAPAs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                      {can('capa:create') ? (
                        <div className="space-y-2">
                          <p>No CAPAs found</p>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCreateDialogOpen(true)}
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Create New CAPA
                          </Button>
                        </div>
                      ) : (
                        <p>No CAPAs found</p>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
          </ReportSection>
        </div>

        <div className="lg:col-span-1">
          <Card className={`bg-white border border-slate-200 shadow-sm sticky top-20 relative ${pidTraceMode ? 'border-cyan-300' : ''}`}>
            <TraceLabel prd="PRD §6.4" req="SM-CAPA-001" />
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold text-slate-800">CAPA Detail</CardTitle>
                {selectedCAPA && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedCAPA(null);
                      navigate('/capa');
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[calc(100vh-280px)]">
                {selectedCAPA ? (
                  <CAPADetail capa={selectedCAPA} />
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <Shield className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>Select a CAPA to view details</p>
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>

      <CAPACreateDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onCreated={handleCAPACreated}
      />
    </div>
  );
};

export default CAPAPage;
